import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axiosClient from '../../api/axiosClient';

function MemberManagement() {
  const { familyUser } = useFamilyAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      
      // FIX: Use cookie-based authentication instead of localStorage token
      if (!familyUser) {
        toast.error('Nicht angemeldet - bitte erneut einloggen');
        return;
      }
      
      console.log('🔐 Loading members for user:', familyUser.name);
      
      const response = await axiosClient.get(`/api/admin/members`, {
        timeout: 10000
      });
      
      setMembers(response.data.members);
      console.log(`✅ ${response.data.members.length} Mitglieder geladen`);
      
    } catch (error) {
      console.error('Error loading members:', error);
      // 401 handling is now automatic via axiosClient interceptor
      if (error.response?.status === 403) {
        toast.error('Admin-Berechtigung erforderlich');
      } else if (error.response?.status === 502) {
        toast.error('Server nicht erreichbar');
      } else {
        toast.error('Fehler beim Laden der Mitglieder');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (memberId, memberEmail) => {
    try {
      setActionLoading(`reset-${memberId}`);
      // Using axiosClient with global withCredentials
      const response = await axiosClient.post(`/api/admin/members/${memberId}/reset-password`, {});
      
      toast.success(`Passwort zurückgesetzt!`, {
        description: `Neues Passwort für ${memberEmail}: ${response.data.temporary_password}`,
        duration: 10000
      });
      
      // Copy to clipboard
      navigator.clipboard.writeText(response.data.temporary_password).then(() => {
        toast.info('Temporäres Passwort in Zwischenablage kopiert');
      });
      
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Fehler beim Passwort-Reset');
    } finally {
      setActionLoading(null);
    }
  };

  const updateRole = async (memberId, newRole) => {
    try {
      setActionLoading(`role-${memberId}`);
      // Using axiosClient with global withCredentials
      await axiosClient.put(`/api/admin/members/${memberId}/role?new_role=${newRole}`, {});
      
      toast.success(`Rolle zu ${newRole === 'admin' ? 'Administrator' : 'Mitglied'} geändert`);
      loadMembers();
      
    } catch (error) {
      console.error('Error updating role:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Rollen-Update';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteMember = async (memberId, memberEmail) => {
    try {
      setActionLoading(`delete-${memberId}`);
      // Using axiosClient with global withCredentials
      await axiosClient.delete(`/api/admin/members/${memberId}`);
      
      toast.success(`Mitglied ${memberEmail} wurde entfernt`);
      loadMembers();
      
    } catch (error) {
      console.error('Error deleting member:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const getRoleBadge = (role) => {
    return role === 'admin' ? (
      <Badge variant="default" className="bg-purple-500 text-white">👑 Administrator</Badge>
    ) : (
      <Badge variant="outline">👤 Mitglied</Badge>
    );
  };

  const getStatusBadge = (onboardingComplete) => {
    return onboardingComplete ? (
      <Badge variant="default" className="bg-green-500 text-white">✅ Aktiv</Badge>
    ) : (
      <Badge variant="destructive">⏳ Pending</Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">👥 Mitgliederverwaltung</h1>
        <p className="text-gray-600">Verwalten Sie die Familienmitglieder und deren Berechtigungen</p>
      </div>

      <div className="grid gap-4">
        {members.map((member) => (
          <Card key={member.id} className={member.id === familyUser?.id ? 'ring-2 ring-purple-200' : ''}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-xl font-semibold text-purple-700">
                      {member.name?.charAt(0) || '👤'}
                    </span>
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {member.name}
                      {member.id === familyUser?.id && (
                        <span className="text-sm text-purple-600 ml-2">(Sie)</span>
                      )}
                    </CardTitle>
                    <CardDescription>{member.email}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getRoleBadge(member.role)}
                  {getStatusBadge(member.has_completed_onboarding)}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 space-y-1">
                  <p>📅 Erstellt: {formatDate(member.created_at)}</p>
                  {member.updated_at && (
                    <p>🔄 Aktualisiert: {formatDate(member.updated_at)}</p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {/* Role Selection */}
                  <Select
                    value={member.role}
                    onValueChange={(newRole) => updateRole(member.id, newRole)}
                    disabled={member.id === familyUser?.id || actionLoading === `role-${member.id}`}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Mitglied</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Password Reset */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={actionLoading === `reset-${member.id}`}
                      >
                        {actionLoading === `reset-${member.id}` ? '🔄' : '🔑'} Passwort
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Passwort zurücksetzen</AlertDialogTitle>
                        <AlertDialogDescription>
                          Ein neues temporäres Passwort wird für <strong>{member.email}</strong> generiert.
                          Der Benutzer muss sich mit dem neuen Passwort anmelden.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetPassword(member.id, member.email)}>
                          Passwort zurücksetzen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  {/* Delete Member */}
                  {member.id !== familyUser?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          disabled={actionLoading === `delete-${member.id}`}
                        >
                          {actionLoading === `delete-${member.id}` ? '🔄' : '🗑️'} Löschen
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Mitglied löschen</AlertDialogTitle>
                          <AlertDialogDescription>
                            Sind Sie sicher, dass Sie <strong>{member.email}</strong> aus der Familie entfernen möchten?
                            Diese Aktion kann nicht rückgängig gemacht werden.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => deleteMember(member.id, member.email)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Endgültig löschen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {members.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">Keine Familienmitglieder gefunden</p>
          </CardContent>
        </Card>
      )}

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">📋 Informationen</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Administratoren</strong> können alle Mitglieder verwalten</li>
          <li>• <strong>Passwort-Reset</strong> generiert ein temporäres Passwort</li>
          <li>• <strong>Rollen-Änderungen</strong> wirken sofort</li>
          <li>• <strong>Löschen</strong> entfernt das Mitglied permanent</li>
        </ul>
      </div>
    </div>
  );
}

export default MemberManagement;